<script setup lang="ts">
    import { computed } from 'vue';
    import { useRoute } from 'vue-router';
    import SvgIcon from '../components/SvgIcon.vue';

    type Locale = 'en' | 'sv' | 'no' | 'da' | 'fi';

    const translations: Record<Locale, Record<string, string>> = {
        en: {
            successTitle: 'Your organization is connected',
            successBody:
                'Single Sign On has been enabled for your users. They can now sign in to Indicate Me with their work accounts. You can close this window.',
            tenantLabel: 'Tenant',
            errorTitle: "We couldn't complete the connection",
            errorBody:
                "Single Sign On didn't confirm consent for your organization. Please try the invitation link again, or contact your Indicate Me representative if the problem persists.",
        },
        sv: {
            successTitle: 'Din organisation är ansluten',
            successBody:
                'Single Sign On är nu aktiverad för era användare. De kan logga in i Indicate Me med sina jobbkonton. Du kan stänga det här fönstret.',
            tenantLabel: 'Klientorganisation',
            errorTitle: 'Vi kunde inte slutföra anslutningen',
            errorBody:
                'Single Sign On bekräftade inte samtycket för din organisation. Försök öppna inbjudningslänken igen, eller kontakta din Indicate Me-kontakt om problemet kvarstår.',
        },
        no: {
            successTitle: 'Organisasjonen din er koblet til',
            successBody:
                'Single Sign On er aktivert for brukerne deres. De kan nå logge inn i Indicate Me med jobbkontoene sine. Du kan lukke dette vinduet.',
            tenantLabel: 'Leietaker',
            errorTitle: 'Vi kunne ikke fullføre tilkoblingen',
            errorBody:
                'Single Sign On bekreftet ikke samtykket for organisasjonen din. Prøv invitasjonslenken på nytt, eller kontakt din Indicate Me-representant hvis problemet vedvarer.',
        },
        da: {
            successTitle: 'Din organisation er forbundet',
            successBody:
                'Single Sign On er aktiveret for jeres brugere. De kan nu logge ind i Indicate Me med deres arbejdskonti. Du kan lukke dette vindue.',
            tenantLabel: 'Lejer',
            errorTitle: 'Vi kunne ikke fuldføre forbindelsen',
            errorBody:
                'Single Sign On bekræftede ikke samtykket for din organisation. Prøv invitationslinket igen, eller kontakt din Indicate Me-repræsentant, hvis problemet fortsætter.',
        },
        fi: {
            successTitle: 'Organisaatiosi on yhdistetty',
            successBody:
                'Single Sign On on otettu käyttöön käyttäjillenne. He voivat nyt kirjautua Indicate Me -palveluun työtileillään. Voit sulkea tämän ikkunan.',
            tenantLabel: 'Vuokraaja',
            errorTitle: 'Yhteyden muodostaminen ei onnistunut',
            errorBody:
                'Single Sign On ei vahvistanut organisaatiosi suostumusta. Yritä kutsulinkkiä uudelleen tai ota yhteyttä Indicate Me -yhteyshenkilöösi, jos ongelma jatkuu.',
        },
    };

    function resolveLocale(raw: string | undefined): Locale {
        const candidate = (raw ?? navigator.language ?? 'en').toLowerCase().split('-')[0];
        const supported: Locale[] = ['en', 'sv', 'no', 'da', 'fi'];
        const aliased = candidate === 'nb' || candidate === 'nn' ? 'no' : candidate;
        return (supported as string[]).includes(aliased) ? (aliased as Locale) : 'en';
    }

    const route = useRoute();
    const locale = computed(() => resolveLocale(route.query.ui_locales as string | undefined));
    const t = computed(() => translations[locale.value]);

    const adminConsent = computed(() => String(route.query.admin_consent ?? '').toLowerCase());
    const error = computed(() => (route.query.error as string) ?? '');
    const errorDescription = computed(() => (route.query.error_description as string) ?? '');
    const tenant = computed(() => (route.query.tenant as string) ?? '');

    const succeeded = computed(() => !error.value && adminConsent.value === 'true');
</script>

<template>
    <div class="flex items-center fixed w-full top-0 left-0 justify-center h-screen p-4 sm:p-8">
        <div
            class="rounded-lg bg-white p-8 shadow-md w-full self-stretch flex items-center justify-center relative"
        >
            <div class="text-center w-full max-w-[28rem]" :lang="locale">
                <template v-if="succeeded">
                    <div class="w-32 mx-auto mb-4 text-primary-carrot/80">
                        <SvgIcon name="check_circle.svg" />
                    </div>
                    <h2 class="heading-lg mb-2">{{ t.successTitle }}</h2>
                    <p class="text-body mb-6">{{ t.successBody }}</p>
                    <p v-if="tenant" class="text-body text-sm opacity-60">
                        {{ t.tenantLabel }}:
                        <span class="font-mono">{{ tenant }}</span>
                    </p>
                </template>
                <template v-else>
                    <div class="w-32 mx-auto mb-4 text-primary-carrot/80">
                        <SvgIcon name="warning-circle.svg" />
                    </div>
                    <h2 class="heading-lg mb-2">{{ t.errorTitle }}</h2>
                    <p class="text-body mb-2">{{ t.errorBody }}</p>
                    <p v-if="errorDescription" class="text-body text-sm opacity-60 mt-4">
                        {{ errorDescription }}
                    </p>
                    <p v-else-if="error" class="text-body text-sm opacity-60 mt-4">
                        {{ error }}
                    </p>
                </template>
            </div>
        </div>
    </div>
</template>
